const suits = ['Sol', 'Mint', 'Burn', 'Stake'];
const ranks = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'];
export const createBaseDeck = () => suits.flatMap((suit) => ranks.map((rank) => ({
    id: `${rank}-${suit}`,
    rank,
    suit,
    baseValue: rankValue(rank),
})));
const rankValue = (rank) => {
    const index = ranks.indexOf(rank);
    if (index === -1)
        return 0;
    return ranks.length - index + 1;
};
/**
 * SHA-256 implementation that works in both browser and Node.js environments.
 * Uses a well-tested pure JavaScript implementation for deterministic hashing.
 */
const sha256 = (message) => {
    const K = [
        0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
        0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
        0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
        0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ];
    const rotr = (n, x) => (x >>> n) | (x << (32 - n));
    const ch = (x, y, z) => (x & y) ^ (~x & z);
    const maj = (x, y, z) => (x & y) ^ (x & z) ^ (y & z);
    const sigma0 = (x) => rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
    const sigma1 = (x) => rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
    const gamma0 = (x) => rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
    const gamma1 = (x) => rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);
    // Convert string to bytes
    const bytes = [];
    for (let i = 0; i < message.length; i++) {
        const code = message.charCodeAt(i);
        if (code < 0x80) {
            bytes.push(code);
        }
        else if (code < 0x800) {
            bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        }
        else {
            bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
    }
    // Padding
    const bitLen = bytes.length * 8;
    bytes.push(0x80);
    while ((bytes.length + 8) % 64 !== 0)
        bytes.push(0);
    for (let i = 56; i >= 0; i -= 8)
        bytes.push((bitLen / Math.pow(2, i)) & 0xff);
    // Initial hash values
    let [h0, h1, h2, h3, h4, h5, h6, h7] = [
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ];
    // Process each 64-byte chunk
    for (let i = 0; i < bytes.length; i += 64) {
        const w = [];
        for (let j = 0; j < 16; j++) {
            w[j] =
                (bytes[i + j * 4] << 24) |
                    (bytes[i + j * 4 + 1] << 16) |
                    (bytes[i + j * 4 + 2] << 8) |
                    bytes[i + j * 4 + 3];
        }
        for (let j = 16; j < 64; j++) {
            w[j] = (gamma1(w[j - 2]) + w[j - 7] + gamma0(w[j - 15]) + w[j - 16]) >>> 0;
        }
        let [a, b, c, d, e, f, g, h] = [h0, h1, h2, h3, h4, h5, h6, h7];
        for (let j = 0; j < 64; j++) {
            const t1 = (h + sigma1(e) + ch(e, f, g) + K[j] + w[j]) >>> 0;
            const t2 = (sigma0(a) + maj(a, b, c)) >>> 0;
            [h, g, f, e, d, c, b, a] = [g, f, e, (d + t1) >>> 0, c, b, a, (t1 + t2) >>> 0];
        }
        [h0, h1, h2, h3, h4, h5, h6, h7] = [
            (h0 + a) >>> 0,
            (h1 + b) >>> 0,
            (h2 + c) >>> 0,
            (h3 + d) >>> 0,
            (h4 + e) >>> 0,
            (h5 + f) >>> 0,
            (h6 + g) >>> 0,
            (h7 + h) >>> 0,
        ];
    }
    // Return as array of 32 bytes
    const result = [];
    for (const val of [h0, h1, h2, h3, h4, h5, h6, h7]) {
        result.push((val >> 24) & 0xff, (val >> 16) & 0xff, (val >> 8) & 0xff, val & 0xff);
    }
    return result;
};
/**
 * Creates a cryptographically secure PRNG using SHA-256 in counter mode.
 * Each call generates a new random value by hashing an incrementing counter
 * with the seed, ensuring unpredictability without knowledge of the seed.
 * Works in both browser and Node.js environments.
 */
const createSecurePRNG = (seed) => {
    let counter = 0;
    return () => {
        // Generate randomness using SHA-256(seed + counter)
        const hash = sha256(`${seed}:${counter}`);
        counter += 1;
        // Convert first 4 bytes to a number in [0, 1)
        const value = ((hash[0] << 24) | (hash[1] << 16) | (hash[2] << 8) | hash[3]) >>> 0;
        return value / 0x100000000;
    };
};
/**
 * Combines server seed, client seed, and nonce using SHA-256 to produce
 * a 256-bit seed with proper entropy mixing.
 */
const combineSeedsSecure = (serverSeed, clientSeed, nonce) => {
    // Combine inputs with SHA-256 for proper entropy mixing
    const hash = sha256(`${serverSeed}:${clientSeed}:${nonce}`);
    return hash.map((b) => b.toString(16).padStart(2, '0')).join('');
};
export const generateDeckOrder = (serverSeed, clientSeed, nonce) => {
    const deck = createBaseDeck();
    // Combine seeds using SHA-256 for proper entropy mixing
    const combinedSeed = combineSeedsSecure(serverSeed, clientSeed, nonce);
    // Create cryptographically secure PRNG from combined seed
    const rng = createSecurePRNG(combinedSeed);
    // Fisher-Yates shuffle with secure randomness
    for (let i = deck.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
};
//# sourceMappingURL=fairness.js.map